/**
 * CacheManageクラス内で利用するメモリ内データ管理機構
 */

/**
 *
 */
class InMemoryCache {
  protected readonly primaryKeyName: string;
  protected readonly secondaryKeyName: string;
  protected readonly getFunc: (string) => any;
  protected dataCache: any = {};
  protected dataCacheAccessTime: any = {};

  /**
   * コンストラクタ
   * @param primaryKeyName
   * @param secondaryKeyName
   * @param getFunc
   */
  constructor(primaryKeyName: string, secondaryKeyName: string, getFunc: (string) => any) {
    this.primaryKeyName = primaryKeyName;
    this.secondaryKeyName = secondaryKeyName;
    this.getFunc = getFunc;
  }

  /**
   * this.dataCacheを更新する。
   * @param data
   * @protected
   */
  public async updateInMemory(data?: any) {
    if (!Array.isArray(data)) {
      // dataが単一のオブジェクトの場合
      const primaryKeyValue = data[this.primaryKeyName];
      // this.dataCacheのデータを更新する。データがなければIndexedDbからデータを読み込む
      if (!this.dataCache[primaryKeyValue]) {
        await this.loadInMemory(primaryKeyValue);
      }
      this.dataCache[primaryKeyValue][data[this.secondaryKeyName]] = data;
      this.dataCacheAccessTime[primaryKeyValue] = new Date().getTime();
      return;
    }

    // dataがarrayの場合
    let primaryKeys: string[] = data.map((item) => item[this.primaryKeyName]);
    primaryKeys = [...new Set(primaryKeys)];
    for (const primaryKeyValue of primaryKeys) {
      // this.dataCacheのデータを更新する。データがなければIndexedDbからデータを読み込む
      if (!this.dataCache[primaryKeyValue]) {
        await this.loadInMemory(primaryKeyValue);
      }
      data.forEach((d: any) => (this.dataCache[primaryKeyValue][d[this.secondaryKeyName]] = d));
      this.dataCacheAccessTime[primaryKeyValue] = new Date().getTime();
    }
  }

  /**
   * this.dataCacheにIndexedDbからデータを読み込んでくる
   * @param primaryKeyValue
   * @protected
   */
  public async loadInMemory(primaryKeyValue: string) {
    if (this.dataCache[primaryKeyValue]) return;

    // EXP: 管理するprimaryKeyの数が多くなったら古いものを削除する
    if (Object.keys(this.dataCache).length >= 10) {
      const minKey = Object.keys(this.dataCacheAccessTime).reduce((acc: string, d: string) =>
        this.dataCacheAccessTime[acc] > this.dataCacheAccessTime[d] ? d : acc
      );
      delete this.dataCache[minKey];
      delete this.dataCacheAccessTime[minKey];
    }

    // EXP: this.dataCache[primaryKeyValue]にデータを読み込む
    this.dataCache[primaryKeyValue] = {};
    const allEntries = await this.getFunc(primaryKeyValue);
    if (!allEntries || allEntries.length === 0) return;
    allEntries.forEach((ent: any) => {
      this.dataCache[primaryKeyValue][ent[this.secondaryKeyName]] = ent;
    });

    this.dataCacheAccessTime[primaryKeyValue] = new Date().getTime();
  }

  /**
   * this.dataCacheからデータを削除する
   * secondaryKeyValueを指定しなければ、primaryKeyのデータ全体を削除する
   * @param primaryKeyValue
   * @param secondaryKeyValue
   * @protected
   */
  public deleteInMemory(primaryKeyValue: string, secondaryKeyValue?: string) {
    if (!this.dataCache[primaryKeyValue]) return;
    if (secondaryKeyValue) {
      if (this.dataCache[primaryKeyValue][secondaryKeyValue]) {
        delete this.dataCache[primaryKeyValue][secondaryKeyValue];
        this.dataCacheAccessTime[primaryKeyValue] = new Date().getTime();
      }
    } else {
      delete this.dataCache[primaryKeyValue];
      delete this.dataCacheAccessTime[primaryKeyValue];
    }
  }

  /**
   * データを取得する。なければIndexedDBからロードしてから探す。
   * @param primaryKeyValue
   * @param secondaryKeyValue
   */
  public async getInMemory(primaryKeyValue: string, secondaryKeyValue: string): Promise<any> {
    if (!this.dataCache[primaryKeyValue]) {
      await this.loadInMemory(primaryKeyValue);
    }
    if (!this.dataCache[primaryKeyValue]) return;
    return this.dataCache[primaryKeyValue][secondaryKeyValue];
  }

  /**
   *
   * @param primaryKeyValue
   */
  public getAllForKey(primaryKeyValue: string): any[] {
    if (!this.dataCache[primaryKeyValue]) return [];
    return Object.values(this.dataCache[primaryKeyValue]);
  }

  /**
   *
   * @param primaryKeyValue
   */
  public getSortedArray(primaryKeyValue: string): any[] {
    if (!this.dataCache[primaryKeyValue]) return [];
    return Object.values(this.dataCache[primaryKeyValue]).sort((a: any, b: any) => a.updatedAt - b.updatedAt);
  }

  /**
   *
   * @param primaryKeyValue
   * @param gsiKeyName
   */
  public getSortedArrayOfGsi(primaryKeyValue: string, gsiKeyName: string): any[] {
    let entries: any = [];
    for (const primaryKey in this.dataCache) {
      if (primaryKey === primaryKeyValue) continue;
      entries = [...entries, ...Object.values(this.dataCache[primaryKey]).filter((d: any) => d[gsiKeyName] === primaryKeyValue)];
    }
    const uniqueEntries = Array.from(new Map(entries.map((ent) => [ent.dataId, ent])).values());
    return uniqueEntries;
  }
}

export { InMemoryCache };
